import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  ArrowLeft, Building2, CreditCard, TrendingUp, TrendingDown,
  Euro, Calendar, Search, Filter, RefreshCw, Loader2,
  PieChart, BarChart3, FileText, AlertTriangle, Check, X,
  Wallet, Banknote, Receipt, ArrowUpRight, ArrowDownRight
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_LABELS = {
  frais_bancaires: { label: 'Frais bancaires', color: 'text-red-400', bg: 'bg-red-500/20' },
  commission_decouvert: { label: 'Commission découvert', color: 'text-orange-400', bg: 'bg-orange-500/20' },
  interets: { label: 'Intérêts', color: 'text-blue-400', bg: 'bg-blue-500/20' },
  abonnement_bancaire: { label: 'Abonnement banque', color: 'text-purple-400', bg: 'bg-purple-500/20' },
  carte_bancaire: { label: 'Carte bancaire', color: 'text-sky-400', bg: 'bg-sky-500/20' },
  virement_emis: { label: 'Virement émis', color: 'text-red-400', bg: 'bg-red-500/20' },
  virement_recu: { label: 'Virement reçu', color: 'text-emerald-400', bg: 'bg-emerald-500/20' },
  prelevement_sepa: { label: 'Prélèvement SEPA', color: 'text-amber-400', bg: 'bg-amber-500/20' },
  cheque_emis: { label: 'Chèque émis', color: 'text-cyan-400', bg: 'bg-cyan-500/20' },
  urssaf: { label: 'URSSAF / Cotisations', color: 'text-pink-400', bg: 'bg-pink-500/20' },
  impots_taxes: { label: 'Impôts & Taxes', color: 'text-rose-400', bg: 'bg-rose-500/20' },
  salaires: { label: 'Salaires', color: 'text-green-400', bg: 'bg-green-500/20' },
  electricite: { label: 'Électricité', color: 'text-yellow-400', bg: 'bg-yellow-500/20' },
  telecom: { label: 'Télécom', color: 'text-indigo-400', bg: 'bg-indigo-500/20' },
  assurance: { label: 'Assurance', color: 'text-teal-400', bg: 'bg-teal-500/20' },
  loyer: { label: 'Loyer', color: 'text-slate-400', bg: 'bg-slate-500/20' },
  autre: { label: 'Autres', color: 'text-gray-400', bg: 'bg-gray-500/20' }
};

export default function BankTrackingPage() {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  
  const [loading, setLoading] = useState(true);
  const [transactions, setTransactions] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [pendingDocuments, setPendingDocuments] = useState([]);
  const [extracting, setExtracting] = useState(null);

  useEffect(() => {
    loadData();
    loadPendingDocuments();
  }, [selectedMonth]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [txRes, dashRes] = await Promise.all([
        axios.get(`${API}/api/bank/transactions`, {
          params: { month: selectedMonth },
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/api/bank/dashboard`, {
          params: { month: selectedMonth },
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setTransactions(txRes.data.transactions || []);
      setDashboard(dashRes.data.stats || null);
    } catch (error) {
      console.error('Erreur chargement:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const loadPendingDocuments = async () => {
    try {
      // Charger les relevés bancaires non encore extraits
      const response = await axios.get(`${API}/api/accounting/documents`, {
        params: { month: selectedMonth },
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const docs = response.data.items || response.data.documents || [];
      const bankDocs = docs.filter(d => 
        (d.suggested_category?.toLowerCase().includes('banc') || 
         d.title?.toLowerCase().includes('relevé') ||
         d.title?.toLowerCase().includes('extrait')) &&
        !d.bank_extracted
      );
      
      setPendingDocuments(bankDocs);
    } catch (error) {
      console.error('Erreur chargement documents:', error);
    }
  };

  const extractTransactions = async (docId) => {
    setExtracting(docId);
    try {
      const response = await axios.post(`${API}/api/bank/transactions/extract`, null, {
        params: { document_id: docId },
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`${response.data.transactions_extracted} transactions extraites`);
      loadData();
      loadPendingDocuments();
    } catch (error) {
      console.error('Erreur extraction:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'extraction');
    } finally {
      setExtracting(null);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(amount || 0);
  };

  const formatMonth = (monthStr) => {
    const [year, month] = monthStr.split('-');
    const months = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 
                    'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
    return `${months[parseInt(month) - 1]} ${year}`;
  };

  const filteredTransactions = transactions.filter(tx => {
    const matchesSearch = !searchTerm || 
      tx.label?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = !selectedCategory || tx.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4 sm:p-6">
      <div className="max-w-7xl mx-auto">
        
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button 
              onClick={() => navigate('/accounting')} 
              variant="ghost" 
              className="text-white/70 hover:text-white"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-white flex items-center gap-2">
                <Building2 className="w-6 h-6 text-blue-400" />
                Suivi Banque
              </h1>
              <p className="text-white/60 text-sm">Analyse détaillée de vos opérations bancaires</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <input
              type="month"
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              className="bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white"
            />
            <Button onClick={loadData} variant="outline" className="border-white/20 text-white">
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Documents en attente d'extraction */}
        {pendingDocuments.length > 0 && (
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 mb-6">
            <h3 className="text-amber-300 font-medium flex items-center gap-2 mb-3">
              <AlertTriangle className="w-5 h-5" />
              {pendingDocuments.length} relevé(s) bancaire(s) à analyser
            </h3>
            <div className="space-y-2">
              {pendingDocuments.map(doc => (
                <div key={doc.id} className="flex items-center justify-between bg-white/5 rounded-lg p-3">
                  <div className="flex items-center gap-3">
                    <FileText className="w-5 h-5 text-amber-400" />
                    <span className="text-white">{doc.title}</span>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => extractTransactions(doc.id)}
                    disabled={extracting === doc.id}
                    className="bg-amber-500 hover:bg-amber-600"
                  >
                    {extracting === doc.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <RefreshCw className="w-4 h-4 mr-1" />
                        Extraire
                      </>
                    )}
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-blue-400" />
          </div>
        ) : (
          <>
            {/* Dashboard Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              {/* Total Débits */}
              <div className="bg-gradient-to-br from-red-500/20 to-red-600/10 rounded-xl p-5 border border-red-500/20">
                <div className="flex items-center gap-3 mb-2">
                  <ArrowDownRight className="w-5 h-5 text-red-400" />
                  <span className="text-white/70">Total Débits</span>
                </div>
                <div className="text-2xl font-bold text-red-400">
                  {formatCurrency(dashboard?.total_debit || 0)}
                </div>
              </div>

              {/* Total Crédits */}
              <div className="bg-gradient-to-br from-emerald-500/20 to-emerald-600/10 rounded-xl p-5 border border-emerald-500/20">
                <div className="flex items-center gap-3 mb-2">
                  <ArrowUpRight className="w-5 h-5 text-emerald-400" />
                  <span className="text-white/70">Total Crédits</span>
                </div>
                <div className="text-2xl font-bold text-emerald-400">
                  {formatCurrency(dashboard?.total_credit || 0)}
                </div>
              </div>

              {/* Frais Bancaires */}
              <div className="bg-gradient-to-br from-orange-500/20 to-orange-600/10 rounded-xl p-5 border border-orange-500/20">
                <div className="flex items-center gap-3 mb-2">
                  <Receipt className="w-5 h-5 text-orange-400" />
                  <span className="text-white/70">Frais Bancaires</span>
                </div>
                <div className="text-2xl font-bold text-orange-400">
                  {formatCurrency(dashboard?.frais_bancaires?.total || 0)}
                </div>
                <div className="text-xs text-white/50 mt-1">
                  Frais: {formatCurrency(dashboard?.frais_bancaires?.frais_bancaires || 0)} | 
                  Comm: {formatCurrency(dashboard?.frais_bancaires?.commission_decouvert || 0)} | 
                  Abo: {formatCurrency(dashboard?.frais_bancaires?.abonnement_bancaire || 0)}
                </div>
              </div>

              {/* Nombre de transactions */}
              <div className="bg-gradient-to-br from-blue-500/20 to-blue-600/10 rounded-xl p-5 border border-blue-500/20">
                <div className="flex items-center gap-3 mb-2">
                  <BarChart3 className="w-5 h-5 text-blue-400" />
                  <span className="text-white/70">Transactions</span>
                </div>
                <div className="text-2xl font-bold text-blue-400">
                  {dashboard?.transactions_count || 0}
                </div>
              </div>
            </div>

            {/* Charges Récurrentes */}
            {dashboard?.charges_recurrentes?.total > 0 && (
              <div className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 rounded-xl border border-purple-500/20 p-5 mb-6">
                <h3 className="text-white font-medium mb-4 flex items-center gap-2">
                  <Receipt className="w-5 h-5 text-purple-400" />
                  Charges Récurrentes
                  <span className="ml-auto text-xl font-bold text-purple-400">
                    {formatCurrency(dashboard.charges_recurrentes.total)}
                  </span>
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                  {dashboard.charges_recurrentes.urssaf > 0 && (
                    <div className="bg-pink-500/20 rounded-lg p-3">
                      <div className="text-pink-400 text-sm">URSSAF</div>
                      <div className="text-white font-bold">{formatCurrency(dashboard.charges_recurrentes.urssaf)}</div>
                    </div>
                  )}
                  {dashboard.charges_recurrentes.impots_taxes > 0 && (
                    <div className="bg-rose-500/20 rounded-lg p-3">
                      <div className="text-rose-400 text-sm">Impôts & Taxes</div>
                      <div className="text-white font-bold">{formatCurrency(dashboard.charges_recurrentes.impots_taxes)}</div>
                    </div>
                  )}
                  {dashboard.charges_recurrentes.electricite > 0 && (
                    <div className="bg-yellow-500/20 rounded-lg p-3">
                      <div className="text-yellow-400 text-sm">Électricité</div>
                      <div className="text-white font-bold">{formatCurrency(dashboard.charges_recurrentes.electricite)}</div>
                    </div>
                  )}
                  {dashboard.charges_recurrentes.telecom > 0 && (
                    <div className="bg-indigo-500/20 rounded-lg p-3">
                      <div className="text-indigo-400 text-sm">Télécom</div>
                      <div className="text-white font-bold">{formatCurrency(dashboard.charges_recurrentes.telecom)}</div>
                    </div>
                  )}
                  {dashboard.charges_recurrentes.assurance > 0 && (
                    <div className="bg-teal-500/20 rounded-lg p-3">
                      <div className="text-teal-400 text-sm">Assurance</div>
                      <div className="text-white font-bold">{formatCurrency(dashboard.charges_recurrentes.assurance)}</div>
                    </div>
                  )}
                  {dashboard.charges_recurrentes.loyer > 0 && (
                    <div className="bg-slate-500/20 rounded-lg p-3">
                      <div className="text-slate-400 text-sm">Loyer</div>
                      <div className="text-white font-bold">{formatCurrency(dashboard.charges_recurrentes.loyer)}</div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Répartition par catégorie */}
            {dashboard?.by_category && Object.keys(dashboard.by_category).length > 0 && (
              <div className="bg-white/5 rounded-xl border border-white/10 p-5 mb-6">
                <h3 className="text-white font-medium mb-4 flex items-center gap-2">
                  <PieChart className="w-5 h-5 text-purple-400" />
                  Répartition par catégorie
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                  {Object.entries(dashboard.by_category).map(([cat, data]) => {
                    const catInfo = CATEGORY_LABELS[cat] || CATEGORY_LABELS.autre;
                    return (
                      <div 
                        key={cat} 
                        className={`${catInfo.bg} rounded-lg p-3 cursor-pointer hover:opacity-80 transition-opacity`}
                        onClick={() => setSelectedCategory(selectedCategory === cat ? '' : cat)}
                      >
                        <div className={`text-sm font-medium ${catInfo.color}`}>
                          {catInfo.label}
                        </div>
                        <div className="text-white font-bold">
                          {formatCurrency(Math.abs(data.total))}
                        </div>
                        <div className="text-white/50 text-xs">
                          {data.count} opération(s)
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Filtres et recherche */}
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <div className="relative flex-1 max-w-xs">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                <Input
                  placeholder="Rechercher..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 bg-white/5 border-white/10 text-white"
                />
              </div>
              
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white"
              >
                <option value="">Toutes catégories</option>
                {Object.entries(CATEGORY_LABELS).map(([key, val]) => (
                  <option key={key} value={key}>{val.label}</option>
                ))}
              </select>
            </div>

            {/* Liste des transactions */}
            <div className="bg-white/5 rounded-xl border border-white/10 overflow-hidden">
              <div className="p-4 border-b border-white/10">
                <h3 className="text-white font-medium">
                  Transactions ({filteredTransactions.length})
                </h3>
              </div>
              
              {filteredTransactions.length === 0 ? (
                <div className="p-8 text-center text-white/50">
                  <Wallet className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>Aucune transaction pour cette période</p>
                  <p className="text-sm mt-1">Importez un relevé bancaire et cliquez sur "Extraire"</p>
                </div>
              ) : (
                <div className="divide-y divide-white/5">
                  {filteredTransactions.map(tx => {
                    const catInfo = CATEGORY_LABELS[tx.category] || CATEGORY_LABELS.autre;
                    const isDebit = tx.amount < 0;
                    
                    return (
                      <div key={tx.id} className="p-4 hover:bg-white/5 transition-colors">
                        <div className="flex items-center gap-4">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isDebit ? 'bg-red-500/20' : 'bg-emerald-500/20'}`}>
                            {isDebit ? (
                              <ArrowDownRight className="w-5 h-5 text-red-400" />
                            ) : (
                              <ArrowUpRight className="w-5 h-5 text-emerald-400" />
                            )}
                          </div>
                          
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-white font-medium truncate">{tx.label}</span>
                              <span className={`text-xs px-2 py-0.5 rounded-full ${catInfo.bg} ${catInfo.color}`}>
                                {catInfo.label}
                              </span>
                            </div>
                            <div className="text-white/50 text-sm">
                              {tx.date} {tx.bank_name && `• ${tx.bank_name}`}
                            </div>
                          </div>
                          
                          <div className={`text-lg font-bold ${isDebit ? 'text-red-400' : 'text-emerald-400'}`}>
                            {isDebit ? '' : '+'}{formatCurrency(tx.amount)}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
